import { ExtendServicePath } from 'shared/models/service';
import authenticationRedirectHandler from 'modules/core/auth/authenticationRedirectHandler';
import { HttpClientResponse } from './Definitions';

/*
  If AosEndpoint is updated in ExecutorFactory.ts, please update the pathname /v1/deploy in here as well.
  We got an error "DEPLOY is undefined" if we try to import AosEndpoint.
*/
const aosDeployPath = `${ExtendServicePath.AOS}/v1/deploy`;

// DevToolsServerExecutor is a subdomain-routed executor (ExtendSubdomainService), unlike AosExecutor's
// path-routed ExtendServicePath, so there's no equivalent service-path constant to prefix here - this
// pathname is matched directly against the resolved request URL.
export const prismPath = '/v1/prism';
export const devAgentAiUsagePath = '/devagent/v1/invocations';
export const customAgentModelsPath = '/v1/custom-agent-models';
const FORBIDDEN_CODE = 403;

export interface Interceptor {
  success?: (resp: HttpClientResponse) => Promise<HttpClientResponse>;
  error?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
    err: {} | { body: any; response: Response }
  ) => Promise<{} | HttpClientResponse>;
}

/**
 * Redirects on auth errors like every other request, except a 403 from any of `forbiddenPassthroughPaths`,
 * which is rejected as-is so the caller can handle it in place instead of navigating the user away.
 */
const createForbiddenPassthroughInterceptor = (...forbiddenPassthroughPaths: string[]): Interceptor => ({
  error: async (err) => {
    const response = 'response' in err ? err.response : undefined;
    const isPassthrough =
      response?.status === FORBIDDEN_CODE && forbiddenPassthroughPaths.some((path) => response.url?.includes(path));
    if (response && !isPassthrough) {
      await authenticationRedirectHandler(response.status);
    }
    return Promise.reject(err);
  },
});

// The usage icon is optional; its entitlement 403 must not make the Developer Agent unavailable.
export const authenticationInterceptor = createForbiddenPassthroughInterceptor(devAgentAiUsagePath);

/**
 * aosAuthenticationInterceptor is created to handle the requirement not to redirect to another page for handling error in AOS deployment endpoint
 * if the user doesn't have access to deploy, refer to JIRA WCPX-9125. Instead the error will be catched in the higher component and handled appropriately ie. showing a toast.
 * For other errors in AOS endpoints, we will keep the same behaviour; redirecting to another page.
 */
export const aosAuthenticationInterceptor = createForbiddenPassthroughInterceptor(aosDeployPath);

/**
 * dtsAuthenticationInterceptor skips the redirect-to-forbidden-page behavior for a 403 from any
 * Prism endpoint, so a tenant user without import permission gets an in-page message instead of being
 * navigated away from the import modal. The query and tags endpoints are fetched together when the modal
 * opens, so both need the carve-out - a 403 on either one alone would still redirect the whole page away.
 * The custom agent models list is also 403 whenever the model provider is unavailable or the user lacks
 * desktop access; the Model picker then falls back to the Auto tiers.
 */
export const dtsAuthenticationInterceptor = createForbiddenPassthroughInterceptor(prismPath, customAgentModelsPath);
